import type { HomeProject } from '../api/home.api';
import type { ColumnDatum } from '@/shared/components/chart/ColumnChart';
import type { DensityBucket } from '@/shared/components/chart/DensityStrip';
import { median } from '@/shared/components/chart/scale';
import {
  addDaysToDateInput,
  formatDayMonth,
  formatMonthLabel,
  formatMonthShort,
  monthKey,
  toDateInputValue,
} from '@/shared/lib/dates';
import type { AgendaProject, StatusCounts } from '@/shared/types/domain';
import type { DecisionRecord } from '../api/home.api';

/**
 * A aritmética da Início: como uma lista de projetos vira coluna, semana e
 * ranking.
 *
 * Funções puras, com `now` sempre injetável — não porque teste seja mais fácil
 * assim (é), mas porque **toda pergunta desta tela é relativa a hoje** e um
 * `new Date()` escondido dentro de uma agregação é um bug que só aparece na
 * virada do mês, em produção, sem ninguém olhando.
 *
 * Tudo que envolve fuso passa por `shared/lib/dates` (regra 9). Nenhuma destas
 * funções constrói data por conta própria.
 */

/** Quantos meses o gráfico de volume olha para trás e para a frente. */
export const MONTHS_BACK = 6;
export const MONTHS_AHEAD = 3;

/** Quantas janelas de sete dias a faixa de densidade cobre. */
export const DENSITY_WEEKS = 8;

/**
 * A janela de consulta do volume, como o `fetchHomeVolume` a espera.
 *
 * Vive aqui junto de quem consome o resultado para que a janela pedida ao banco
 * e a janela desenhada no eixo **não possam divergir** — se divergirem, a
 * primeira coluna fica pela metade e ninguém percebe, porque uma coluna baixa
 * parece um mês fraco.
 */
export function volumeRange(now: Date): { from: string; to: string } {
  const { year, month } = splitMonthKey(monthKey(now));
  const first = shiftMonth(year, month, -MONTHS_BACK);
  const afterLast = shiftMonth(year, month, MONTHS_AHEAD + 1);
  return {
    from: `${isoMonthStart(first)}T00:00:00.000Z`,
    to: `${isoMonthStart(afterLast)}T00:00:00.000Z`,
  };
}

/**
 * Uma coluna por mês da janela — **inclusive os meses sem projeto nenhum**.
 *
 * O mês vazio é o ponto do gráfico, não uma falha dele: "não teve nada em maio"
 * é a resposta que a pessoa veio buscar. Montar as colunas a partir dos dados
 * (`group by`) apagaria maio do eixo e faria abril encostar em junho — o
 * gráfico ficaria sem buraco nenhum e a série pareceria contínua.
 */
export function monthColumns(projects: readonly HomeProject[], now: Date): ColumnDatum[] {
  const byMonth = new Map<string, StatusCounts>();
  for (const project of projects) {
    const key = monthKey(project.starts_at);
    const counts = byMonth.get(key) ?? {};
    counts[project.status] = (counts[project.status] ?? 0) + 1;
    byMonth.set(key, counts);
  }

  const { year, month } = splitMonthKey(monthKey(now));
  const currentKey = monthKey(now);

  return Array.from({ length: MONTHS_BACK + MONTHS_AHEAD + 1 }, (_, index) => {
    const target = shiftMonth(year, month, index - MONTHS_BACK);
    const key = `${target.year}-${String(target.month).padStart(2, '0')}`;
    const counts = byMonth.get(key) ?? {};
    const total = Object.values(counts).reduce((sum, value) => sum + (value ?? 0), 0);
    // Dia 15 ao meio-dia UTC: qualquer hora do meio do mês cai no mesmo mês em
    // São Paulo, e assim o rótulo nunca escorrega para o mês vizinho.
    const sample = new Date(`${isoMonthStart(target).slice(0, 8)}15T12:00:00.000Z`);

    return {
      key,
      label: formatMonthShort(sample).replace('.', ''),
      description: `${formatMonthLabel(sample)}: ${total === 1 ? '1 projeto' : `${total} projetos`}`,
      counts,
      current: key === currentKey,
    };
  });
}

/**
 * A janela da densidade: de hoje até `DENSITY_WEEKS` semanas à frente.
 */
export function densityRange(now: Date): { from: string; to: string } {
  const today = toDateInputValue(now);
  return {
    from: `${today}T00:00:00.000Z`,
    to: `${addDaysToDateInput(today, DENSITY_WEEKS * 7)}T00:00:00.000Z`,
  };
}

/**
 * Oito janelas de sete dias, contando compromissos da igreja.
 *
 * ⚠️ **Janela ROLANTE a partir de hoje, não semana de calendário.** Uma semana
 * de calendário aberta numa sexta-feira mostraria dois dias restantes como se
 * fossem sete: a primeira coluna nasceria artificialmente baixa e a leitura
 * "a semana que vem está mais cheia" seria produzida pelo calendário, não pelos
 * dados. Rolante, toda coluna cobre exatamente sete dias.
 *
 * ⚠️ **Conta a linha, não o status.** É a contrapartida do que a
 * `agenda_projects` faz no servidor: ela colapsa `changes_requested` em
 * `under_review` para quem não é dono, e desenhar aqui uma distinção por
 * situação seria redesenhar o que o banco acabou de apagar. Ver o docblock do
 * `DensityStrip`.
 */
export function weekBuckets(items: readonly AgendaProject[], now: Date): DensityBucket[] {
  const today = toDateInputValue(now);

  return Array.from({ length: DENSITY_WEEKS }, (_, index) => {
    const start = addDaysToDateInput(today, index * 7);
    const end = addDaysToDateInput(today, (index + 1) * 7);
    const count = items.filter((item) => {
      if (!item.starts_at) return false;
      const day = toDateInputValue(item.starts_at);
      return day >= start && day < end;
    }).length;

    const label = formatDayMonth(`${start}T12:00:00.000Z`);
    return {
      key: start,
      label,
      description: `Semana de ${label}: ${count === 1 ? '1 compromisso' : `${count} compromissos`} na igreja`,
      count,
      to: start,
    };
  });
}

export interface MinistryActivity {
  ministryId: string;
  name: string;
  total: number;
}

/**
 * Quanto cada ministério moveu na janela, e quais não moveram nada.
 *
 * ⚠️ **As duas listas vêm de fontes com escopos DIFERENTES, e é por isso que o
 * bloco que as usa é restrito a quem enxerga a coordenação inteira.** Os nomes
 * vêm de `list_ministries_with_leaders` (recorte `my_coordination_ids()`); as
 * contagens, de `projects_with_context` (recorte `my_ministry_ids()`). Para
 * coordenador, overseer e admin os dois conjuntos coincidem. Para líder e
 * auxiliar **divergem**: eles recebem os ministérios IRMÃOS da própria
 * coordenação e não podem ler projeto nenhum deles — todo irmão apareceria como
 * "sem projeto", que é uma afirmação falsa sobre a igreja, feita com dado que a
 * pessoa não tem.
 *
 * O invariante é *"as duas fontes coincidem"*, e quem o garante hoje é o gate
 * por `readOwnCoordination` na página. A `ability` é conveniência de UX e podia
 * afrouxar sem ninguém lembrar deste parágrafo — por isso há teste de
 * composição prendendo os dois.
 *
 * ⚠️ **"Sem projeto ENVIADO", nunca "sem atividade".** A RLS esconde rascunho de
 * ministério sem vínculo direto, então um ministério que só tem rascunho chega
 * aqui com zero. Não é vazamento — é subcontagem —, mas é uma afirmação que
 * pode estar errada, e a palavra escolhida é a que continua verdadeira nos dois
 * casos: rascunho não é projeto enviado.
 */
export function ministryActivity(
  projects: readonly HomeProject[],
  ministries: readonly { ministry_id: string; ministry_name: string }[],
): { ranked: MinistryActivity[]; silent: MinistryActivity[] } {
  const totals = new Map<string, number>();
  for (const project of projects) {
    totals.set(project.ministry_id, (totals.get(project.ministry_id) ?? 0) + 1);
  }

  const all = ministries.map((ministry) => ({
    ministryId: ministry.ministry_id,
    name: ministry.ministry_name,
    total: totals.get(ministry.ministry_id) ?? 0,
  }));

  return {
    ranked: all
      .filter((entry) => entry.total > 0)
      .sort((a, b) => b.total - a.total || a.name.localeCompare(b.name, 'pt-BR')),
    silent: all
      .filter((entry) => entry.total === 0)
      .sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')),
  };
}

export interface PipelineStats {
  /** Dias entre envio e decisão. `null` quando não houve decisão na janela. */
  medianDays: number | null;
  decidedCount: number;
}

/**
 * Mediana até decisão e volume de decisões da janela (PRD §6.3, meta ≤ 7 dias).
 *
 * Dias de calendário e não horas: é a unidade em que a meta foi escrita e em que
 * a liderança fala. `Math.max(0, …)` protege contra o caso degenerado de um
 * `decided_at` anterior ao `submitted_at` — que o banco não deveria produzir, e
 * que como número negativo puxaria a mediana para baixo em silêncio.
 */
export function pipelineStats(decisions: readonly DecisionRecord[]): PipelineStats {
  const days = decisions.map((decision) =>
    Math.max(
      0,
      (Date.parse(decision.decided_at) - Date.parse(decision.submitted_at)) / 86_400_000,
    ),
  );
  return { medianDays: median(days), decidedCount: days.length };
}

// ── Aritmética de mês, sem biblioteca de data ────────────────────────────────

interface YearMonth {
  year: number;
  month: number;
}

function splitMonthKey(key: string): YearMonth {
  const [year, month] = key.split('-');
  return { year: Number(year), month: Number(month) };
}

/** Soma meses tratando o mês como base 12 — o `Date` faria isso no fuso errado. */
function shiftMonth(year: number, month: number, delta: number): YearMonth {
  const zeroBased = year * 12 + (month - 1) + delta;
  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
}

function isoMonthStart({ year, month }: YearMonth): string {
  return `${year}-${String(month).padStart(2, '0')}-01`;
}
